import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  Alert,
  CircularProgress,
} from "@mui/material";
import "./TextScreen.css";
import "./DataDeletionScreen.css";
import logo from "../assets/logo.png";
import PoweredByTipify from "../components/PoweredByTipify";
import LanguageSelector from "../components/LanguageSelector";
import {
  postDataDeletionRequest,
  postDataDeletionConfirm,
} from "../api/privacy";

const TIPOS = ["tiper", "empleado", "responsable"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function currentLanguage(i18n) {
  return (i18n.language || "es").split("-")[0].toLowerCase();
}

/** Cabecera común: logo de Tipify y selector de idioma (como /politica-privacidad). */
function Header() {
  return (
    <div className="header">
      <div className="logo-container mt-3">
        <img src={logo} alt="Tipify Logo" className="logo2" />
      </div>
      <LanguageSelector />
    </div>
  );
}

/**
 * Página pública de solicitud de eliminación de datos (/eliminar-datos, /delete-data).
 * URL que figura en Google Play como "solicitud de eliminación de datos".
 */
export const DataDeletionScreen = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [tipo, setTipo] = useState("tiper");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError(t("dataDeletion.errors.invalidEmail"));
      return;
    }
    setSending(true);
    try {
      await postDataDeletionRequest({ email: trimmed, tipo, language: currentLanguage(i18n) });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (status === 429) setError(t("dataDeletion.errors.tooMany"));
      else if (code === "INVALID_EMAIL") setError(t("dataDeletion.errors.invalidEmail"));
      else setError(t("dataDeletion.errors.generic"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Container className="container-fluid data-deletion">
      <Header />
      <h1>{t("dataDeletion.title")}</h1>
      <p>{t("dataDeletion.intro")}</p>

      <h2>{t("dataDeletion.tiperTitle")}</h2>
      <p>{t("dataDeletion.tiperDeleted")}</p>
      <p>{t("dataDeletion.tiperKept")}</p>

      <h2>{t("dataDeletion.staffTitle")}</h2>
      <p>{t("dataDeletion.staffText")}</p>

      <p className="data-deletion-deadline">{t("dataDeletion.deadline")}</p>

      {sent ? (
        <Alert severity="success" className="data-deletion-box">
          <strong>{t("dataDeletion.sentTitle")}</strong>
          <div>{t("dataDeletion.sentText")}</div>
        </Alert>
      ) : (
        <form className="data-deletion-box" onSubmit={onSubmit} noValidate>
          <TextField
            label={t("dataDeletion.emailLabel")}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            margin="normal"
          />
          <FormControl margin="normal">
            <FormLabel id="data-deletion-tipo">{t("dataDeletion.typeLabel")}</FormLabel>
            <RadioGroup
              aria-labelledby="data-deletion-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              {TIPOS.map((value) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={<Radio />}
                  label={t(`dataDeletion.types.${value}`)}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={sending}
            fullWidth
            size="large"
          >
            {sending ? <CircularProgress size={24} color="inherit" /> : t("dataDeletion.submit")}
          </Button>
        </form>
      )}

      <p className="data-deletion-footer">
        {t("dataDeletion.contact")}{" "}
        <a href="mailto:legal@tipify.tips">legal@tipify.tips</a>
        {" · "}
        <Link to="/politica-privacidad">{t("dataDeletion.privacyLink")}</Link>
      </p>
      <PoweredByTipify />
    </Container>
  );
};

/** /eliminar-datos/confirmar?token=…: confirma la solicitud y muestra el resultado. */
export const DataDeletionConfirmScreen = () => {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const lang = params.get("lang");
  const [state, setState] = useState({ loading: true });
  // El enlace es de un solo uso: evitar la doble llamada de StrictMode.
  const calledRef = useRef(false);

  useEffect(() => {
    if (lang && currentLanguage(i18n) !== lang) i18n.changeLanguage(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    if (!token) {
      setState({ loading: false, errorCode: "MISSING_TOKEN" });
      return;
    }
    postDataDeletionConfirm(token)
      .then((res) => setState({ loading: false, result: res.data }))
      .catch((err) =>
        setState({
          loading: false,
          errorCode: err?.response?.data?.code || (err?.response?.status === 429 ? "TOO_MANY" : "GENERIC"),
        })
      );
  }, [token]);

  let content;
  if (state.loading) {
    content = (
      <div className="data-deletion-box" style={{ textAlign: "center" }}>
        <CircularProgress />
        <p>{t("dataDeletion.confirm.loading")}</p>
      </div>
    );
  } else if (state.result?.status === "completed") {
    content = (
      <Alert severity="success" className="data-deletion-box">
        <strong>{t("dataDeletion.confirm.completedTitle")}</strong>
        <div>{t("dataDeletion.confirm.completedText")}</div>
      </Alert>
    );
  } else if (state.result) {
    content = (
      <Alert severity="info" className="data-deletion-box">
        <strong>{t("dataDeletion.confirm.verifiedTitle")}</strong>
        <div>{t("dataDeletion.confirm.verifiedText")}</div>
      </Alert>
    );
  } else {
    const key = {
      EXPIRED: "expired",
      INVALID_TOKEN: "invalid",
      MISSING_TOKEN: "invalid",
      TOO_MANY: "tooMany",
    }[state.errorCode] || "generic";
    content = (
      <Alert severity="error" className="data-deletion-box">
        <div>{t(`dataDeletion.confirm.errors.${key}`)}</div>
        <Link to="/eliminar-datos">{t("dataDeletion.confirm.newRequest")}</Link>
      </Alert>
    );
  }

  return (
    <Container className="container-fluid data-deletion">
      <Header />
      <h1>{t("dataDeletion.title")}</h1>
      {content}
      <p className="data-deletion-footer">
        {t("dataDeletion.contact")}{" "}
        <a href="mailto:legal@tipify.tips">legal@tipify.tips</a>
      </p>
      <PoweredByTipify />
    </Container>
  );
};

export default DataDeletionScreen;
